import { LitElement, html, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-radio-group.styles';
import { SDS_SIZES } from '../types';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

type RadioGroupSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;
const updatableRadioProps = ['disabled', 'errorMessage', 'readOnly', 'required', 'size'] as const;
type UpdatableRadioProp = (typeof updatableRadioProps)[number];

import '../sds-radio/sds-radio';
import '../sds-icon/sds-icon';
import '../sds-alert/sds-alert';
import { SDSRadio } from '../sds-radio/sds-radio';

/**
 * TODO: Implement keyboard navigation behavior according to
 * https://www.w3.org/WAI/ARIA/apg/patterns/radio/
 */

export interface SDSRadioGroupProps {
	legend?: string;
	'help-text'?: string;
	'error-message'?: string;
	readonly?: boolean;
	direction?: 'column' | 'row';
	disabled?: boolean;
	size?: RadioGroupSize;
	required?: boolean;
}

@guardedCustomElement('sds-radio-group')
export class SDSRadioGroup extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];

	@property({ type: String }) legend?: SDSRadioGroupProps['legend'];
	@property({ type: String, attribute: 'help-text' }) helpText?: SDSRadioGroupProps['help-text'];
	@property({ type: String, attribute: 'error-message' }) errorMessage?: SDSRadioGroupProps['error-message'];
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: SDSRadioGroupProps['readonly'] = false;
	@property({ type: String }) direction: 'column' | 'row' = 'column';
	@property({ type: Boolean, reflect: true }) disabled: SDSRadioGroupProps['disabled'] = false;
	@property({ type: Boolean, reflect: true }) required: SDSRadioGroupProps['required'] = false;
	@property({ type: String, reflect: true }) size: SDSRadioGroupProps['size'] = 'md';

	@state() private _selectedValue: string | null = null;
	@query('slot') private _slotElement!: HTMLSlotElement;

	private readonly _handleRadioChange = (event: Event): void => {
		const target = event.composedPath().find((el) => el instanceof HTMLElement && el.tagName === 'SDS-RADIO') as HTMLElement & { value: string };

		if (target) {
			this._selectedValue = target.getAttribute('value') || null;
			this.dispatchSheriffEvent('sds-update');
		}
	};

	private _handleSlotChange = () => {
		this._updateChildProps([...updatableRadioProps]);
	};

	private _getChildRadios(): SDSRadio[] {
		return this._slotElement?.assignedElements({ flatten: true }).filter((element): element is SDSRadio => element.tagName === 'SDS-RADIO') ?? [];
	}

	private _updateChildProps(changedAttributes: Array<UpdatableRadioProp>) {
		const radios = this._getChildRadios();

		for (const childRadio of radios) {
			for (const attribute of changedAttributes) {
				switch (attribute) {
					case 'readOnly':
						childRadio.readOnly = this.readOnly;
						break;
					case 'disabled':
						childRadio.parentRequestDisabled = this.disabled;
						break;
					case 'errorMessage':
						childRadio.error = Boolean(this.errorMessage);
						break;
					case 'required':
						childRadio.required = this.required;
						break;
					case 'size':
						childRadio.size = this.size;
						break;
				}
			}
		}
	}

	override updated(changedProps: Map<string | number | symbol, unknown>) {
		super.updated(changedProps);

		const changedKeys = Array.from(changedProps.keys());
		if (changedKeys.includes('errorMessage') || changedKeys.includes('error-message')) {
			this._updateChildProps(['errorMessage']);
		}
	}

	override attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
		super.attributeChangedCallback(name, _old, value);

		// DOM attribute for readonly has different casing than our types
		if (name === 'readonly') {
			this._updateChildProps(['readOnly']);
			return;
		}

		const property = name as UpdatableRadioProp;
		if (updatableRadioProps.includes(property)) {
			this._updateChildProps([property]);
		}
	}

	override render() {
		const radioGroupClasses = {
			'sds-radio-group': true,
			'sds-radio-group--readonly': this.readOnly ?? false,
			'sds-radio-group--disabled': this.disabled ?? false,
		};

		const radioClasses = {
			'sds-radio-group__radios': true,
			'sds-radio-group--column': this.direction === 'column',
			'sds-radio-group--row': this.direction === 'row',
		};

		return html`
			<fieldset class="${classMap(radioGroupClasses)}">
				${this.legend
					? html`<legend class="sds-radio-group__legend">${this.readOnly ? html`<sds-icon name="lock-material-filled" size="md"></sds-icon>` : nothing}${this.legend}</legend>`
					: nothing}
				${this.helpText ? html`<p class="sds-radio-group__help-text">${this.helpText}</p>` : nothing}
				<div class="${classMap(radioClasses)}" @change=${this._handleRadioChange}>
					<slot @slotchange=${this._handleSlotChange}></slot>
				</div>
				${this.errorMessage
					? html`<sds-alert variant="secondary" data-palette="danger">
							<p slot="message">${this.errorMessage}</p>
						</sds-alert>`
					: nothing}
			</fieldset>
		`;
	}

	get value(): {} {
		return { selectedValue: this._selectedValue };
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-radio-group': SDSRadioGroup;
	}
}
